import './SecondaryClock.scss';
import {useEffect, useState} from 'react';
import {
  ClockChannelDto,
  ClockStation_ChannelInfoModel,
  ClockStation_ChannelsStateModel,
  ClockStation_ImpulseModel,
  ClockStation_ChannelTimeModel,
} from '../../../api';
import {
  Channel, clockFaceSrc, hourHandSrc, minuteHandSrc,
  MODEL_OPTIONS,
  SecondaryClockParams,
  SecondaryClockProps,
} from './SecondaryClock.model';
import {SecondaryClockApi} from '../../../services/SecondaryClock.api';
import {ClockChimeSettingsApi} from '../../../services/ClockChimeSettings.api';
import {
  DatePicker,
  IconButton,
  Input,
  InputGroup,
  InputNumber,
  RadioTile,
  RadioTileGroup,
  toaster,
  Toggle,
  Tooltip,
  useMediaQuery,
  Whisper
} from 'rsuite';
import {SecondaryClockUtils} from './SecondaryClock.utils';
import TimeInput from '../../shared/TimeInput/TimeInput';
import {FaClock} from 'react-icons/fa';
import UpdateButton from '../../shared/UpdateButton/UpdateButton';
import {getTooltip} from '../../../shared/Tooltip';
import moment from "moment";
import NotificationFactory from "../../../shared/Notifications";
import {IoCloseCircleOutlineIcon, TfiSaveIcon, TiFlowMergeIcon, TiFlowParallelIcon} from "../../../models/Icons.model";

const SecondaryClock = ({deviceId, isAdmin, channels = 0}: SecondaryClockProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<SecondaryClockParams | null>(null);
  const [channelTimes, setChannelTimes] = useState<Record<number, { hours: number; minutes: number }>>({});
  const [impulses, setImpulses] = useState<Record<number, number>>({});
  const [enabledMap, setEnabledMap] = useState<Record<number, boolean>>({});
  const [isCombinedMode, setIsCombinedMode] = useState<boolean>(false);
  const [details, setDetails] = useState<Record<number, ClockChannelDto | null>>({});
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [technicalDescription, setTechnicalDescription] = useState('');
  const [technicalDescriptionLoading, setTechnicalDescriptionLoading] = useState(false);

  const [isInline] = useMediaQuery('xl');

  useEffect(() => {
    loadInitialData().then();
  }, [deviceId, channels]);

  useEffect(() => {
    if (isAdmin) void loadTechnicalDescription();
  }, [deviceId, isAdmin]);

  const loadTechnicalDescription = async () => {
    setTechnicalDescriptionLoading(true);
    try {
      const response = await ClockChimeSettingsApi.getClockChimeDescription(deviceId);
      setTechnicalDescription(response?.technicalDescription ?? '');
    } catch (err) {
      console.error('Ошибка получения технической информации', err);
    } finally {
      setTechnicalDescriptionLoading(false);
    }
  };

  const saveTechnicalDescription = async () => {
    setTechnicalDescriptionLoading(true);
    try {
      await ClockChimeSettingsApi.updateClockChimeDescription(deviceId, {technicalDescription});
      toaster.push(NotificationFactory.success('Техническая информация сохранена'), {placement: 'topEnd'});
    } catch (err) {
      console.error('Ошибка сохранения технической информации', err);
      toaster.push(NotificationFactory.error('Не удалось сохранить техническую информацию'), {placement: 'topEnd'});
    } finally {
      setTechnicalDescriptionLoading(false);
    }
  };

  const loadInitialData = async () => {
    setParams(null);
    setDetails({});
    setChannelTimes({});
    setImpulses({});
    setEnabledMap({});
    setIsCombinedMode(false);

    await getParams();
  };

  const getParams = async () => {
    setLoading(true);
    try {
      const response: SecondaryClockParams = await SecondaryClockApi.getParams(deviceId);
      setParams(response);

      const initialTimes: Record<number, { hours: number; minutes: number }> = {};
      const initialImpulses: Record<number, number> = {};
      const initialEnabled: Record<number, boolean> = {};
      const channelsCount = (response.channels ?? []).length;

      (response.channels ?? []).forEach((channel: Channel, index: number) => {
        initialTimes[index] = {
          hours: channel.arrowPosition?.hour ?? 0,
          minutes: channel.arrowPosition?.minute ?? 0,
        };
        initialImpulses[index] = channel.impulseDuration ?? 0;
        initialEnabled[index] = Boolean(channel.enabled);
      });

      setChannelTimes(initialTimes);
      setImpulses(initialImpulses);
      setEnabledMap(initialEnabled);
      setIsCombinedMode(Boolean(response.isCombinedMode));

      await loadDetailsForAllChannels(channelsCount);
    } catch (err) {
      console.error('Ошибка получения параметров вторичных часов', err);
      setParams(null);
      setChannelTimes({});
      setImpulses({});
      setEnabledMap({});
      setIsCombinedMode(false);
      await loadDetailsForAllChannels(channels);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailsForAllChannels = async (channelsCount: number) => {
    if (channelsCount <= 0) {
      setDetails({});
      return;
    }

    setDetailsLoading(true);
    try {
      const promises: Promise<ClockChannelDto | null>[] = [];

      for (let index = 0; index < channelsCount; index++) {
        const channelNumber = index + 1;
        const p = SecondaryClockApi.getDetails(deviceId, channelNumber)
          .then((data) => data as ClockChannelDto)
          .catch((err) => {
            console.error('Ошибка получения деталей канала', channelNumber, err);
            return null;
          });
        promises.push(p);
      }

      const results = await Promise.all(promises);

      const detailsMap: Record<number, ClockChannelDto | null> = {};
      results.forEach((item, idx) => {
        detailsMap[idx] = item;
      });

      setDetails(detailsMap);
    } finally {
      setDetailsLoading(false);
    }
  };

  const onImpulseChanged = (channelIndex: number, value: number | null) => {
    if (value == null) return;
    setImpulses(prev => ({...prev, [channelIndex]: value}));
  };

  const updateImpulse = async (channelIndex: number) => {
    const num = impulses[channelIndex] ?? 0;

    const payload: ClockStation_ImpulseModel = {
      channel: channelIndex + 1,
      impulse: num ? num * 10 : 0,
    };

    try {
      const ok: boolean = await SecondaryClockApi.updateImpulse(deviceId, payload);
      if (ok) {
        await getParams();
      }
    } catch (err) {
      console.error('Ошибка при обновлении импульса', err);
    }
  };

  const handleChannelTimeChange = (channelIndex: number, hours: number, minutes: number) => {
    setChannelTimes(prev => ({
      ...prev,
      [channelIndex]: {hours, minutes},
    }));
  };

  const handleSaveChannelTime = async (channelIndex: number) => {
    const time = channelTimes[channelIndex];
    if (!time) return;

    const payload: ClockStation_ChannelTimeModel = {
      channel: channelIndex + 1,
      arrowPosition: {
        hoursPosition: time.hours,
        minutesPosition: time.minutes,
      },
    };

    try {
      const ok = await SecondaryClockApi.updateChannelsArrows(deviceId, payload);
      if (ok) {
        await getParams();
      }
    } catch (err) {
      console.error('Ошибка при обновлении положения стрелок', err);
    }
  };

  const sendChannelsState = async (nextEnabledMap: Record<number, boolean>, nextIsCombinedMode: boolean) => {
    if (!params) return;

    setLoading(true);

    const channelsPayload: ClockStation_ChannelInfoModel[] = (params.channels ?? []).map((ch, index) => ({
      enabled: nextEnabledMap[index] ?? Boolean(ch.enabled),
      isSyncEnabled: false,
    }));

    const payload: ClockStation_ChannelsStateModel = {
      channels: channelsPayload,
      isCombinedMode: nextIsCombinedMode,
    };

    try {
      const ok = await SecondaryClockApi.updateChannelState(deviceId, payload);
      if (ok) {
        toaster.push(
          NotificationFactory.success("Успешно"),
          {placement: "topEnd"}
        );
        await getParams();
      }
    } catch (err) {
      toaster.push(
        NotificationFactory.error("Ошибка при обновлении состояния каналов"),
        {placement: "topEnd"}
      );
      console.error('Ошибка при обновлении состояния каналов', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChannel = async (channelIndex: number, checked: boolean) => {
    setEnabledMap(prev => {
      const next = {...prev, [channelIndex]: checked};
      void sendChannelsState(next, isCombinedMode);
      return next;
    });
  };

  const handleDetailChange = (
    channelIndex: number,
    field: keyof ClockChannelDto,
    value: string | null
  ) => {
    setDetails(prev => ({
      ...prev,
      [channelIndex]: {
        ...(prev[channelIndex] ?? {
          model: null,
          serialNumber: null,
          installationDate: null,
          installationLocation: null,
        }),
        [field]: value,
      },
    }));
  };

  const handleSaveChannelDetails = async (channelIndex: number) => {
    const channelDetails = details[channelIndex];
    if (!channelDetails) return;

    const channelNumber = channelIndex + 1;

    const payload: ClockChannelDto = {
      model: channelDetails.model || '',
      serialNumber: channelDetails.serialNumber || '',
      installationDate: channelDetails.installationDate
        ? moment(channelDetails.installationDate).format('YYYY-MM-DD')
        : '',
      installationLocation: channelDetails.installationLocation || '',
    };

    try {
      const ok = await SecondaryClockApi.updateChannelsDetails(
        deviceId,
        channelNumber,
        payload
      );
      if (ok) {
        toaster.push(NotificationFactory.success("Детали сохранены"), {placement: "topEnd"});
        await loadDetailsForAllChannels(params?.channels?.length ?? channels);
      }
    } catch (err) {
      console.error('Ошибка при обновлении деталей канала', err);
    }
  };

  const clearChannelDetails = (channelIndex: number) => {
    setDetails(prev => ({
      ...prev,
      [channelIndex]: {
        model: '',
        serialNumber: '',
        installationDate: '',
        installationLocation: '',
      },
    }));
  };

  const handleModeChange = async (value: any) => {
    const stringValue = String(value);
    const nextIsCombined = stringValue === 'combined';

    setIsCombinedMode(nextIsCombined);
    await sendChannelsState(enabledMap, nextIsCombined);
  };

  const hasParamsChannels = Boolean(params?.channels?.length);
  const channelsForRender: Channel[] = params?.channels ?? Array.from(
    {length: channels},
    () => ({} as Channel)
  );


  // if (!params) {
  //   return (
  //     <div className="fieldset-wrapper mt-3">
  //       <div className="legend-title main-legend-title">Вторичные часы</div>
  //       <div className="p-3">
  //         <Loader/> Загрузка параметров вторичных часов...
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <>
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Вторичные часы</div>
        <section className="secondary-clock">
          <div className="row secondary-clock-wrapper mt-4">
            {channelsForRender.map((channel: Channel, index: number) => {
              const channelName = SecondaryClockUtils.getChannelDisplayName(index);
              const {hourAngle, minuteAngle} = SecondaryClockUtils.calculateHandAngles(
                channel.arrowPosition?.hour ?? 0,
                channel.arrowPosition?.minute ?? 0
              );

              const isChannelEnabled = hasParamsChannels
                ? (enabledMap[index] ?? Boolean(channel.enabled))
                : false;
              const impulseValue = impulses[index] ?? (channel.impulseDuration ?? 0);
              const isToggleLoading = false;
              const isReadonlyByParams = !hasParamsChannels;

              const channelDetails = details[index];

              if (!hasParamsChannels) {
                return (
                  <div key={channelName} className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
                    <div className="fieldset-wrapper control border-blue h-100 bg-white mt-0 pt-2">
                      <div className="legend-title">{channelName}</div>
                      <div className="p-2">
                        {detailsLoading ? (
                          <span className="text-muted">Загрузка деталей...</span>
                        ) : (
                          <>
                            <InputGroup className="mb-2">
                              <InputGroup.Addon>Модель механизма</InputGroup.Addon>
                              <div style={{width: '100%'}}>
                                <select
                                  className="form-control form-select"
                                  value={channelDetails?.model ?? ''}
                                  disabled={detailsLoading || !isAdmin}
                                  onChange={(e) => handleDetailChange(index, 'model', e.target.value || null)}
                                >
                                  {MODEL_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </div>
                              {isAdmin && <InputGroup.Addon><TfiSaveIcon style={{cursor: 'pointer'}} onClick={() => handleSaveChannelDetails(index)}/></InputGroup.Addon>}
                            </InputGroup>
                            <InputGroup className="mb-2">
                              <InputGroup.Addon>Серийный номер</InputGroup.Addon>
                              <Input
                                value={channelDetails?.serialNumber ?? ''}
                                disabled={detailsLoading || !isAdmin}
                                onChange={(value) => handleDetailChange(index, 'serialNumber', value || null)}
                              />
                              {isAdmin && <InputGroup.Addon><TfiSaveIcon style={{cursor: 'pointer'}} onClick={() => handleSaveChannelDetails(index)}/></InputGroup.Addon>}
                            </InputGroup>
                            <InputGroup className="mb-2">
                              <InputGroup.Addon>Дата установки</InputGroup.Addon>
                              <DatePicker
                                className="w-100"
                                format="dd.MM.yyyy"
                                cleanable={false}
                                disabled={detailsLoading || !isAdmin}
                                value={
                                  channelDetails?.installationDate
                                    ? new Date(channelDetails.installationDate)
                                    : null
                                }
                                onChange={(value) => handleDetailChange(index, 'installationDate', value ? moment(value).format('YYYY-MM-DD') : null)}
                              />
                              {isAdmin && <InputGroup.Addon><TfiSaveIcon style={{cursor: 'pointer'}} onClick={() => handleSaveChannelDetails(index)}/></InputGroup.Addon>}
                            </InputGroup>
                            <InputGroup>
                              <InputGroup.Addon>Место установки</InputGroup.Addon>
                              <Input
                                value={channelDetails?.installationLocation ?? ''}
                                disabled={detailsLoading || !isAdmin}
                                onChange={(value) => handleDetailChange(index, 'installationLocation', value || null)}
                              />
                              {isAdmin && <InputGroup.Addon><TfiSaveIcon style={{cursor: 'pointer'}} onClick={() => handleSaveChannelDetails(index)}/></InputGroup.Addon>}
                            </InputGroup>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={channelName} className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
                  <div
                    className="fieldset-wrapper control border-blue h-100 bg-white mt-0 pt-2"
                    style={{
                      border: !isChannelEnabled ? '1px solid grey' : '',
                      opacity: isChannelEnabled ? 1 : 0.3,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <div className="legend-title">{channelName}</div>
                    <div className="clock-wrapper w-100">
                      <div className="d-flex justify-content-between align-items-center mb-1 w-100">
                        <Toggle
                          style={{ maxWidth: 55 }}
                          color="blue"
                          checkedChildren="ON"
                          unCheckedChildren="OFF"
                          loading={isToggleLoading}
                          checked={isChannelEnabled}
                          disabled={isToggleLoading || loading || isReadonlyByParams}
                          onChange={(checked: boolean) => handleToggleChannel(index, checked)}
                        />
                        <UpdateButton loading={loading} onRefresh={getParams} />
                      </div>
                      <div className="clock-container">
                        <div className="clock mt-1" style={{position: 'relative'}}>
                          <img
                            className="clock-face"
                            src={clockFaceSrc}
                            alt="clock face"
                          />
                          <img
                            className="clock-hand hour-hand"
                            src={hourHandSrc}
                            alt="hour hand"
                            style={{transform: `rotate(${hourAngle}deg)`}}
                          />
                          <img
                            className="clock-hand minute-hand"
                            src={minuteHandSrc}
                            alt="minute hand"
                            style={{transform: `rotate(${minuteAngle}deg)`}}
                          />
                        </div>
                        <div className="row">
                          <div className="col-md-12">
                            <span className="clock-location">{channelDetails?.installationLocation ?? ''}</span>
                          </div>
                        </div>
                      </div>

                      <div className="w-100">
                        <div className="row">
                          <div className="col-md-12" style={{padding: 3, paddingTop: 0}}>
                            <InputGroup style={{padding: 0}}>
                              <InputGroup.Addon>Положение стрелок</InputGroup.Addon>
                              <div style={{flex: 1}}>
                                <TimeInput
                                  hours={channelTimes[index]?.hours ?? channel.arrowPosition?.hour ?? 0}
                                  minutes={channelTimes[index]?.minutes ?? channel.arrowPosition?.minute ?? 0}
                                  maxHoursValue={11}
                                  onTimeChanged={({hours, minutes}: { hours: number; minutes: number }) => {
                                    handleChannelTimeChange(index, Number(hours), Number(minutes));
                                  }}
                                  width={'100%'}
                                />
                              </div>
                              <InputGroup.Addon>
                                <TfiSaveIcon
                                  style={{cursor: isReadonlyByParams ? 'default' : 'pointer'}}
                                  onClick={() => !isReadonlyByParams && handleSaveChannelTime(index)}
                                />
                              </InputGroup.Addon>
                            </InputGroup>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-xxl-3 col-xl-3 col-lg-6 col-md-3 col-sm-3 col-6" style={{padding: 3}}>
                            <div className="input-group">
                              <div className="channel-input-group-text input-group-text">
                                <label className="channel-form-label form-label">
                                  Напряж.
                                </label>
                              </div>
                              <input
                                className="channel-form-control form-control clock-control"
                                readOnly
                                value={params?.voltage ?? ''}
                                style={SecondaryClockUtils.getVoltageStyleByState(channel.state ?? '')}
                              />
                              <div className="channel-input-group-text input-group-text input-group-text-wrapper">
                                <div>В</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-xxl-3 col-xl-3 col-lg-6 col-md-3 col-sm-3 col-6" style={{padding: 3}}>
                            <div className="input-group">
                              <div className="channel-input-group-text input-group-text">
                                <div className="channel-form-label form-label input-group-text-wrapper">
                                  <div>Ток</div>
                                </div>
                              </div>
                              <input
                                className="channel-form-control form-control clock-control"
                                readOnly
                                value={channel.current ?? ''}
                              />
                              <div className="channel-input-group-text input-group-text input-group-text-wrapper">
                                <div>A</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 col-xxl-6 col-xl-6 col-lg-12 col-md-6 col-sm-6" style={{padding: 3}}>
                            <InputGroup>
                              <InputGroup.Addon style={{fontSize:12}}>Длительность</InputGroup.Addon>
                              <InputNumber
                                style={{height: 31}}
                                value={impulseValue}
                                step={0.1}
                                min={0.02}
                                max={24.0}
                                disabled={isReadonlyByParams}
                                onChange={(value) => onImpulseChanged(index, value as number)}
                              />
                              <InputGroup.Addon>
                                <TfiSaveIcon
                                  style={{cursor: isReadonlyByParams ? 'default' : 'pointer'}}
                                  onClick={() => !isReadonlyByParams && updateImpulse(index)}
                                />
                              </InputGroup.Addon>
                            </InputGroup>
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <label
                              className="time-label mb-2 mt-2 d-inline-block"
                              style={{textAlign: 'center'}}>
                              Детали
                            </label>
                            <Whisper
                              placement="topStart"
                              controlId="control-id-hover"
                              trigger="hover"
                              speaker={getTooltip('Очистить форму деталей')}
                            >
                            <span className="d-inline-block">
                                <IoCloseCircleOutlineIcon
                                  className="close-icon"
                                  onClick={() => clearChannelDetails(index)}/>
                            </span>
                            </Whisper>
                          </div>


                          {/* Модель механизма */}
                          <div className="col-md-12" style={{padding: 3, paddingTop: 0}}>
                            <InputGroup style={{padding: 0}}>
                              <InputGroup.Addon>Модель механизма</InputGroup.Addon>
                              <div style={{width: '100%'}}>
                                <select
                                  style={{borderRadius: 0, border: 'none', marginLeft: 10, fontSize: 14}}
                                  className="form-control form-select"
                                  name="model"
                                  value={channelDetails?.model ?? ''}
                                  onChange={(e) =>
                                    handleDetailChange(index, 'model', e.target.value || null)
                                  }
                                  disabled={detailsLoading}
                                >

                                  {MODEL_OPTIONS.map((x, idx) => (
                                    <option key={idx} value={x.value} style={{fontSize: 14}}>
                                      {x.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {isAdmin
                                ? (
                                <InputGroup.Addon>
                                  <TfiSaveIcon
                                    style={{cursor: 'pointer'}}
                                    onClick={() => handleSaveChannelDetails(index)}
                                  />
                                </InputGroup.Addon>
                              )
                              : (<div style={{width: 20}}></div>)}
                            </InputGroup>
                          </div>
                        </div>

                        {/* Серийный номер */}
                        <div className="row">
                          <div className="col-md-12" style={{padding: 3, paddingTop: 0}}>
                            <InputGroup style={{padding: 0}}>
                              <InputGroup.Addon style={{minWidth: 155}}>Серийный номер</InputGroup.Addon>
                              <Input
                                value={channelDetails?.serialNumber ?? ''}
                                onChange={(value) =>
                                  handleDetailChange(index, 'serialNumber', value || null)
                                }
                                disabled={detailsLoading}
                              />
                              {isAdmin && (
                                <InputGroup.Addon>
                                  <TfiSaveIcon
                                    style={{cursor: 'pointer'}}
                                    onClick={() => handleSaveChannelDetails(index)}
                                  />
                                </InputGroup.Addon>
                              )}

                            </InputGroup>
                          </div>
                        </div>

                        {/* Дата установки */}
                        <div className="row">
                          <div className="col-md-12" style={{padding: 3, paddingTop: 0}}>
                            <InputGroup style={{padding: 0}}>
                              <InputGroup.Addon style={{minWidth: 155}}>Дата установки</InputGroup.Addon>
                              <DatePicker
                                className="w-100"
                                format="dd.MM.yyyy"
                                caretAs={FaClock as any}
                                loading={loading || detailsLoading}
                                value={
                                  channelDetails?.installationDate
                                    ? new Date(channelDetails.installationDate)
                                    : null
                                }
                                cleanable={false}
                                onChange={(value) =>
                                  handleDetailChange(
                                    index,
                                    'installationDate',
                                    value ? value.toISOString() : null
                                  )
                                }
                              />
                              {isAdmin && (
                                <InputGroup.Addon>
                                  <TfiSaveIcon
                                    style={{cursor: 'pointer'}}
                                    onClick={() => handleSaveChannelDetails(index)}
                                  />
                                </InputGroup.Addon>
                              )}
                            </InputGroup>
                          </div>
                        </div>

                        {/* Место установки */}
                        <div className="row">
                          <div className="col-md-12" style={{padding: 3, paddingTop: 0}}>
                            <Whisper
                              placement="topStart"
                              controlId="control-id-hover"
                              trigger="hover"
                              speaker={getTooltip('Текст не более 15 символов')}
                            >
                              <InputGroup style={{padding: 0}}>
                                <InputGroup.Addon style={{minWidth: 155}}>Место установки</InputGroup.Addon>
                                <Input
                                  value={channelDetails?.installationLocation ?? ''}
                                  onChange={(value) =>
                                    handleDetailChange(
                                      index,
                                      'installationLocation',
                                      value || null
                                    )
                                  }
                                  disabled={detailsLoading}
                                />
                                <InputGroup.Addon>
                                  <TfiSaveIcon
                                    style={{cursor: 'pointer'}}
                                    onClick={() => handleSaveChannelDetails(index)}
                                  />
                                </InputGroup.Addon>
                              </InputGroup>
                            </Whisper>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="fieldset-wrapper">
        <div className="legend-title main-legend-title">Режим работы каналов</div>
        <section className="secondary-clock">
          <div className="row p-3 p-2">
              <RadioTileGroup
                inline={isInline}
                aria-label="Channel mode"
                value={isCombinedMode ? 'combined' : 'separate'}
                onChange={handleModeChange}
              >
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 col-sm-12 p-2">
                  <RadioTile
                    disabled={loading || !hasParamsChannels}
                    className="radio-tile"
                    icon={<TiFlowMergeIcon />}
                    label="Совместный режим"
                    value="combined"
                  >
                    <span className="radio-tile-text">Каналы работают вместе.  Длительность импульса берется из первого канала ( {params?.channels?.[0]?.impulseDuration ?? 0} ).  Ток потребления суммируется.</span>
                  </RadioTile>
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 col-sm-12 p-2">
                  <RadioTile
                    disabled={loading || !hasParamsChannels}
                    icon={<TiFlowParallelIcon />}
                    label="Раздельный режим"
                    value="separate"
                  >
                    <span className="radio-tile-text">
                       Каналы работают по очереди.  Каждому каналу можно назначить свою длительность импульса.
                    </span>

                  </RadioTile>
                </div>
              </RadioTileGroup>
          </div>
        </section>
      </div>

      {isAdmin && (
        <div className="fieldset-wrapper mb-2">
          <div className="legend-title main-legend-title">Техническая информация</div>
          <section className="secondary-clock">
            <div className="row p-2">
              <div className="col-12 d-flex justify-content-end gap-2">
                <Whisper placement="top" speaker={<Tooltip>Сохранить</Tooltip>}>
                  <IconButton
                    style={{marginRight: '10px'}}
                    icon={<TfiSaveIcon/>}
                    size="sm"
                    loading={technicalDescriptionLoading}
                    onClick={saveTechnicalDescription}
                    className="refresh-btn"
                  />
                </Whisper>
                <UpdateButton loading={technicalDescriptionLoading} onRefresh={loadTechnicalDescription}/>
              </div>
            </div>
            <div className="row p-2 pt-0">
              <div className="col-md-12">
                <Input
                  as="textarea"
                  rows={5}
                  value={technicalDescription}
                  disabled={technicalDescriptionLoading}
                  onChange={(value) => setTechnicalDescription(value)}
                />
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default SecondaryClock;
